import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { EASE, inViewProps, MaskLine, Reveal } from "./motion";
import { LogoMark } from "./Logo";
import { StoreBadges } from "./StoreBadges";
import { SubscribeForm } from "./SubscribeForm";
import { LINKS } from "@/lib/site";
import { scrollToId } from "@/lib/lenis";

const COLS = [
  {
    title: "Ascolta",
    links: [
      { l: "Spotify", href: LINKS.spotify, id: "spotify" },
      { l: "Apple Music", href: LINKS.appleMusic, id: "apple-music" },
      { l: "YouTube", href: LINKS.youtube, id: "youtube" },
    ],
  },
  {
    title: "Seguimi",
    links: [
      { l: "Instagram", href: LINKS.instagram, id: "instagram" },
      { l: "TikTok", href: LINKS.tiktok, id: "tiktok" },
      { l: "YouTube", href: LINKS.youtube, id: "youtube-social" },
    ],
  },
  {
    title: "sfushop",
    links: [
      { l: "Drop 01", anchor: "sfushop", id: "drop" },
      { l: "Musica & Bio", anchor: "musica", id: "bio" },
      { l: "L'app", anchor: "app", id: "app" },
    ],
  },
  {
    title: "Info",
    links: [
      { l: "Condizioni di vendita", to: "/condizioni#vendita", id: "terms" },
      { l: "Spedizioni e resi", to: "/condizioni#spedizioni", id: "shipping" },
      { l: "Privacy policy", to: "/condizioni#privacy", id: "privacy" },
    ],
  },
];

const PERKS = ["Flash Pack PDF in regalo", "Codici sconto riservati", "Drop sfushop in anteprima", "Nuove uscite prima di tutti"];

const FooterLink = ({ link }) => {
  const cls = "group relative inline-block py-1 text-left text-bone/80 transition-colors hover:text-acid";
  if (link.to) return <Link to={link.to} data-testid={`footer-link-${link.id}`} className={cls}>{link.l}</Link>;
  return link.href ? (
    <a href={link.href} target="_blank" rel="noreferrer" data-testid={`footer-link-${link.id}`} className={cls}>{link.l}</a>
  ) : (
    <button onClick={() => scrollToId(link.anchor)} data-testid={`footer-link-${link.id}`} className={cls}>{link.l}</button>
  );
};

export const Footer = () => (
  <footer id="crew" data-testid="site-footer" className="relative overflow-hidden border-t border-line px-5 pt-24 sm:px-8 lg:px-12">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-6" data-testid="footer-newsletter-block">
        <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskLine>Sconti e</MaskLine>
          <MaskLine delay={0.1} className="text-acid">nuove uscite.</MaskLine>
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-md text-base leading-relaxed text-bone/70">
            Lasciami la tua email: ti scrivo solo quando conta. Codici sconto riservati sui drop sfushop, accesso anticipato ai pezzi limitati e la notizia di ogni nuova uscita, prima che arrivi ovunque. E subito in regalo il Flash Pack Vol. 01: 6 flash tattoo esclusivi in PDF.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2 border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-bone/80">
                <Check className="h-3 w-3 text-acid" /> {p}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.2} className="mt-8 max-w-lg">
          <SubscribeForm source="newsletter" testidPrefix="footer-newsletter" buttonLabel="Iscriviti" successText="Sei nella lista! Ti scrivo per sconti e nuove uscite." />
          <p className="mt-3 text-xs text-smoke">
            Iscrivendoti accetti la <Link to="/condizioni#privacy" data-testid="footer-privacy-link" className="text-bone/80 underline underline-offset-2 hover:text-acid">Privacy policy</Link>. Puoi disiscriverti quando vuoi.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 lg:col-span-6 lg:col-start-7">
        {COLS.map((c) => (
          <div key={c.title}>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">{c.title}</p>
            <ul className="space-y-1">
              {c.links.map((link) => (
                <li key={link.id}><FooterLink link={link} /></li>
              ))}
            </ul>
          </div>
        ))}
        <div className="col-span-2 sm:col-span-4" data-testid="footer-app-badges">
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Scarica InkMaster, l'app per imparare a tatuare</p>
          <StoreBadges appStoreTestId="footer-app-store-button" googlePlayTestId="footer-google-play-button" />
        </div>
      </div>
    </div>

    <div className="mt-24 flex flex-wrap items-center justify-between gap-4 border-t border-line py-6 font-mono text-[10px] uppercase tracking-[0.22em] text-smoke">
      <span className="flex items-center gap-2"><LogoMark className="h-5 w-5" /> © 2026 SFUMO · sfushop</span>
      <span className="flex flex-wrap items-center gap-6">
        <span>Musica e vestiti indipendenti. Nessuna etichetta, tutti i diritti riservati.</span>
        <Link to="/admin/login" data-testid="footer-admin-link" className="transition-colors hover:text-acid">Area riservata</Link>
      </span>
    </div>

    <motion.div aria-hidden {...inViewProps()} className="pointer-events-none -mb-[4vw] select-none overflow-hidden">
      <motion.p
        variants={{ hidden: { y: "70%", opacity: 0 }, show: { y: "0%", opacity: 1, transition: { duration: 1.6, ease: EASE } } }}
        className="text-outline-soft text-center font-display text-[22vw] font-black leading-[0.85] tracking-[-0.05em]"
      >
        SFUMO
      </motion.p>
    </motion.div>
  </footer>
);
